import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { listPortMeetings, listSchedules, listNoonReports } from '../api/operations';
import { listVessels } from '../api/fleet';

const FALLBACK = {
  vessels: [
    { name: 'Global Highway', cargo_util: 91, lane_meters: 7500, status: 'sea', next_port: 'Baltimore' },
    { name: 'Texas Highway', cargo_util: 94, lane_meters: 7500, status: 'sea', next_port: 'Brunswick' },
    { name: 'Poseidon Highway', cargo_util: 86, lane_meters: 7000, status: 'port', next_port: 'Jacksonville' },
    { name: 'Nereus Highway', cargo_util: 80, lane_meters: 7000, status: 'sea', next_port: 'Veracruz' },
    { name: 'Drive Green Highway', cargo_util: 98, lane_meters: 7500, status: 'sea', next_port: 'Newark' },
  ],
  meetings: [
    { portCode: 'ZEE', portName: 'Zeebrugge', calls: [
      { vessel: 'Texas Highway', trade: 'US East Coast', status: 'risk', estDelay: '3-4 hours', reasons: ['Berth occupied'], stevedores: 22, eta: '16-05 1200', etd: '17-05 0200' },
      { vessel: 'Courageous Highway', trade: 'Asia-Europe', status: 'monitor', estDelay: '1-2 hours', reasons: ['Congestion'], stevedores: 20, eta: '16-05 1800', etd: '17-05 1000' },
    ] },
    { portCode: 'BRE', portName: 'Bremerhaven', calls: [
      { vessel: 'Texas Highway', trade: 'US East Coast', status: 'risk', estDelay: '5-8 hours', reasons: ['Berth occupied'], stevedores: 18, eta: '17-05 1800', etd: '18-05 1000' },
      { vessel: 'Century Highway 2', trade: 'US Gulf', status: 'monitor', estDelay: '9-12 hours', reasons: ['Equipment fault'], stevedores: 0, eta: '19-05 1400', etd: '20-05 0600' },
    ] },
  ],
  schedules: [
    { vessel: 'Global Highway', rows: [{ portCode: 'BRE', portName: 'Bremerhaven', opsHours: 18, psToBerth: 2, etbAdj: 1, commenceAdj: 2, completeAdj: 1, etaAdj: 0 }] },
    { vessel: 'Texas Highway', rows: [{ portCode: 'ZEE', portName: 'Zeebrugge', opsHours: 21, psToBerth: 3, etbAdj: 2, commenceAdj: 4, completeAdj: 2, etaAdj: 2 }] },
    { vessel: 'Poseidon Highway', rows: [{ portCode: 'BAL', portName: 'Baltimore', opsHours: 24, psToBerth: 4, etbAdj: 1, commenceAdj: 5, completeAdj: 3, etaAdj: 1 }] },
  ],
  noon: [
    { vessel: 'Global Highway', cargo_util: 91, created_at: '2026-06-01T10:00:00Z' },
    { vessel: 'Texas Highway', cargo_util: 94, created_at: '2026-06-02T10:00:00Z' },
    { vessel: 'Poseidon Highway', cargo_util: 86, created_at: '2026-06-03T10:00:00Z' },
  ],
};

const delayColor = (hours) => hours >= 8 ? 'var(--red)' : hours >= 3 ? 'var(--orange)' : hours > 0 ? 'var(--yellow)' : 'var(--green)';

function number(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function avg(items) {
  return items.length ? items.reduce((sum, item) => sum + item, 0) / items.length : 0;
}

function delayHours(label) {
  const text = String(label || '').toLowerCase();
  if (!text || text.includes('no delay')) return 0;
  const nums = text.match(/\d+/g)?.map(Number) || [];
  if (nums.length >= 2) return (nums[0] + nums[1]) / 2;
  if (nums.length === 1) return nums[0];
  return text.includes('unknown') ? 1 : 0;
}

function rowDwellHours(row) {
  return number(row.psToBerth) + number(row.etbAdj) + number(row.commenceAdj) + number(row.opsHours) + number(row.completeAdj);
}

function portKey(code, name) {
  return String(code || name || 'TBC').trim() || 'TBC';
}

function normalizeMeeting(row) {
  return {
    portCode: row.port_code || row.portCode || '',
    portName: row.port_name || row.portName || row.port_code || row.portCode || 'Unknown',
    calls: Array.isArray(row.calls) ? row.calls : [],
  };
}

function buildKpis({ schedules, meetings, vessels, noon }) {
  const calls = meetings.flatMap((meeting) => {
    const normalized = normalizeMeeting(meeting);
    return normalized.calls.map((call) => ({
      ...call,
      portCode: normalized.portCode,
      portName: normalized.portName,
      terminal: meeting.terminal || call.terminal || '',
      meetingDate: meeting.meeting_date || meeting.meetingDate || '',
      delayHours: delayHours(call.estDelay),
    }));
  });

  const scheduleRows = schedules.flatMap((schedule) => (schedule.rows || []).map((row) => ({
    ...row,
    vessel: schedule.vessel,
    scheduleId: schedule.id,
    voyageNumber: schedule.voyageNumber || schedule.voyage_number || schedule.voyage || '',
    portCode: row.portCode || row.port_code || '',
    portName: row.portName || row.port_name || row.portCode || row.port_code || 'TBC',
    delayHours: Math.max(0, number(row.etaAdj) + number(row.etbAdj) + number(row.commenceAdj) + number(row.completeAdj)),
    dwellHours: rowDwellHours(row),
  })));

  const utilValues = vessels.map((v) => number(v.cargo_util)).filter(Boolean);
  const noonUtil = noon.map((report) => number(report.cargo_util ?? report.cargoUtil)).filter(Boolean);
  const allUtil = noonUtil.length ? noonUtil : utilValues;

  const loadedLaneMeters = vessels.reduce((sum, v) => sum + number(v.lane_meters) * number(v.cargo_util) / 100, 0);
  const totalLaneMeters = vessels.reduce((sum, v) => sum + number(v.lane_meters), 0);
  const delayedCalls = calls.filter((call) => call.delayHours > 0 || call.status === 'risk');
  const riskCalls = calls.filter((call) => call.status === 'risk' || call.delayHours >= 3);

  const portMap = new Map();
  const getPort = (code, name) => {
    const key = portKey(code, name);
    const item = portMap.get(key) || {
      portCode: key,
      portName: name || key,
      calls: 0,
      delayHours: 0,
      dwell: [],
      risks: 0,
      scheduleRows: [],
      meetingCalls: [],
      vessels: [],
      loadedLaneMeters: 0,
      totalLaneMeters: 0,
      utilValues: [],
    };
    if (name && item.portName === key) item.portName = name;
    portMap.set(key, item);
    return item;
  };

  scheduleRows.forEach((row) => {
    const item = getPort(row.portCode, row.portName);
    item.calls += 1;
    item.delayHours += row.delayHours;
    item.dwell.push(row.dwellHours);
    item.scheduleRows.push(row);
    if (row.delayHours >= 3) item.risks += 1;
  });
  calls.forEach((call) => {
    const item = getPort(call.portCode, call.portName);
    item.calls += 1;
    item.delayHours += call.delayHours;
    item.meetingCalls.push(call);
    if (call.status === 'risk' || call.delayHours >= 3) item.risks += 1;
  });
  vessels.forEach((vessel) => {
    const destination = vessel.next_port || vessel.nextPort || vessel.port || '';
    if (!destination) return;
    const item = getPort('', destination);
    item.vessels.push(vessel);
    item.totalLaneMeters += number(vessel.lane_meters);
    item.loadedLaneMeters += number(vessel.lane_meters) * number(vessel.cargo_util) / 100;
    if (number(vessel.cargo_util)) item.utilValues.push(number(vessel.cargo_util));
  });

  const ports = Array.from(portMap.values()).map((port) => ({
    ...port,
    avgDelay: Number((port.calls ? port.delayHours / port.calls : 0).toFixed(1)),
    avgDwell: Number(avg(port.dwell).toFixed(1)),
    cargoUtil: Math.round(avg(port.utilValues)),
    openCapacity: Math.max(0, Math.round(port.totalLaneMeters - port.loadedLaneMeters)),
    loadedLaneMeters: Math.round(port.loadedLaneMeters),
    totalLaneMeters: Math.round(port.totalLaneMeters),
  })).sort((a, b) => b.avgDelay - a.avgDelay || b.risks - a.risks);

  const cargoTrend = (noon.length ? noon : vessels).slice(-8).map((item, index) => ({
    name: item.created_at ? new Date(item.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : (item.name || `V${index + 1}`),
    util: Math.round(number(item.cargo_util ?? item.cargoUtil)),
  }));

  return {
    avgCargoUtil: Math.round(avg(allUtil)),
    loadedLaneMeters: Math.round(loadedLaneMeters),
    openCapacity: Math.max(0, Math.round(totalLaneMeters - loadedLaneMeters)),
    delayedCalls: delayedCalls.length,
    riskCalls,
    totalCalls: calls.length + scheduleRows.length,
    avgDelay: Number(avg(calls.map((call) => call.delayHours).concat(scheduleRows.map((row) => row.delayHours))).toFixed(1)),
    avgPortStay: Number(avg(scheduleRows.map((row) => row.dwellHours).filter(Boolean)).toFixed(1)),
    ports,
    cargoTrend,
  };
}

function MetricCard({ label, value, sub, color }) {
  return (
    <div style={{ background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 'var(--radius)', padding: 14, minHeight: 96 }}>
      <div style={{ fontSize: 8, color: 'var(--t3)', letterSpacing: 1.4, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: 'var(--syne)', fontSize: 26, fontWeight: 700, color: color || 'var(--text)', marginTop: 8 }}>{value}</div>
      <div style={{ fontSize: 10, color: 'var(--t2)', marginTop: 5 }}>{sub}</div>
    </div>
  );
}

function Panel({ title, right, children }) {
  return (
    <section style={{ background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 'var(--radius)', overflow: 'hidden', minHeight: 0 }}>
      <div style={{ height: 42, borderBottom: '1px solid var(--b1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 14px' }}>
        <div style={{ fontFamily: 'var(--syne)', fontSize: 13, fontWeight: 700 }}>{title}</div>
        {right && <div style={{ fontSize: 10, color: 'var(--t2)' }}>{right}</div>}
      </div>
      {children}
    </section>
  );
}

function PortMetric({ label, value, color }) {
  return (
    <div style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 6, padding: 10, minHeight: 70 }}>
      <div style={{ fontSize: 8, color: 'var(--t3)', letterSpacing: 1.2, textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontFamily: 'var(--syne)', fontSize: 20, fontWeight: 700, color: color || 'var(--text)', marginTop: 6 }}>{value}</div>
    </div>
  );
}

function PortDetail({ port }) {
  if (!port) return null;
  const scheduleRows = port.scheduleRows || [];
  const meetingCalls = port.meetingCalls || [];
  const vessels = port.vessels || [];

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Panel title={`${port.portName} Detail`} right={`${port.portCode} - ${port.calls} tracked calls`}>
        <div style={{ padding: 12, display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))', gap: 10 }}>
            <PortMetric label="Calls" value={port.calls} />
            <PortMetric label="Avg delay" value={`${port.avgDelay}h`} color={delayColor(port.avgDelay)} />
            <PortMetric label="Avg dwell" value={`${port.avgDwell || 0}h`} />
            <PortMetric label="Risks" value={port.risks} color={port.risks ? 'var(--red)' : 'var(--green)'} />
            <PortMetric label="Cargo util" value={port.cargoUtil ? `${port.cargoUtil}%` : '-'} color={port.cargoUtil >= 90 ? 'var(--green)' : port.cargoUtil ? 'var(--yellow)' : undefined} />
            <PortMetric label="Open capacity" value={port.totalLaneMeters ? port.openCapacity.toLocaleString() : '-'} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
            <div style={{ border: '1px solid var(--b1)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '9px 11px', borderBottom: '1px solid var(--b1)', fontSize: 10, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: 1 }}>Scheduled Calls</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <tbody>
                  {scheduleRows.map((row, index) => (
                    <tr key={`${row.vessel}-${row.portCode}-${index}`} style={{ borderBottom: '1px solid rgba(30,48,72,.55)' }}>
                      <td style={{ padding: '9px 11px', fontWeight: 700 }}>{row.vessel || 'TBC'}</td>
                      <td style={{ padding: '9px 11px', color: 'var(--t2)' }}>{row.voyageNumber || 'Voyage TBC'}</td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', color: delayColor(row.delayHours), fontWeight: 700 }}>{row.delayHours}h</td>
                    </tr>
                  ))}
                  {!scheduleRows.length && (
                    <tr><td style={{ padding: 18, color: 'var(--t3)', textAlign: 'center' }}>No schedule rows for this port.</td></tr>
                  )}
                </tbody>
              </table>
            </div>

            <div style={{ border: '1px solid var(--b1)', borderRadius: 6, overflow: 'hidden' }}>
              <div style={{ padding: '9px 11px', borderBottom: '1px solid var(--b1)', fontSize: 10, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: 1 }}>Meeting Calls</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <tbody>
                  {meetingCalls.map((call, index) => (
                    <tr key={`${call.vessel}-${call.portCode}-${index}`} style={{ borderBottom: '1px solid rgba(30,48,72,.55)' }}>
                      <td style={{ padding: '9px 11px', fontWeight: 700 }}>{call.vessel || 'TBC'}</td>
                      <td style={{ padding: '9px 11px', color: 'var(--t2)' }}>{call.trade || call.terminal || 'Port call'}</td>
                      <td style={{ padding: '9px 11px', textAlign: 'right', color: delayColor(call.delayHours), fontWeight: 700 }}>{call.estDelay || `${call.delayHours}h`}</td>
                    </tr>
                  ))}
                  {!meetingCalls.length && (
                    <tr><td style={{ padding: 18, color: 'var(--t3)', textAlign: 'center' }}>No meeting calls for this port.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{ border: '1px solid var(--b1)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '9px 11px', borderBottom: '1px solid var(--b1)', fontSize: 10, color: 'var(--t2)', textTransform: 'uppercase', letterSpacing: 1 }}>Cargo Linked To Port</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>
                {vessels.map((vessel) => (
                  <tr key={vessel.name} style={{ borderBottom: '1px solid rgba(30,48,72,.55)' }}>
                    <td style={{ padding: '9px 11px', fontWeight: 700 }}>{vessel.name}</td>
                    <td style={{ padding: '9px 11px', color: 'var(--t2)' }}>{number(vessel.lane_meters).toLocaleString()} lane meters</td>
                    <td style={{ padding: '9px 11px', textAlign: 'right', color: number(vessel.cargo_util) >= 90 ? 'var(--green)' : 'var(--yellow)', fontWeight: 700 }}>{number(vessel.cargo_util)}%</td>
                  </tr>
                ))}
                {!vessels.length && (
                  <tr><td style={{ padding: 18, color: 'var(--t3)', textAlign: 'center' }}>No vessel cargo profile currently linked to this port.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Panel>
    </div>
  );
}

export default function CargoPortKpis() {
  const [data, setData] = useState({ schedules: [], meetings: [], vessels: [], noon: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPortCode, setSelectedPortCode] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const [schedules, meetings, vessels, noon] = await Promise.all([
          listSchedules(),
          listPortMeetings(),
          listVessels(),
          listNoonReports(),
        ]);
        if (active) setData({ schedules, meetings, vessels, noon });
      } catch (err) {
        if (active) {
          setError(err.message || 'KPI data failed to load.');
          setData(FALLBACK);
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, []);

  const kpis = useMemo(() => {
    const source = {
      schedules: data.schedules.length ? data.schedules : FALLBACK.schedules,
      meetings: data.meetings.length ? data.meetings : FALLBACK.meetings,
      vessels: data.vessels.length ? data.vessels : FALLBACK.vessels,
      noon: data.noon.length ? data.noon : FALLBACK.noon,
    };
    return buildKpis(source);
  }, [data]);

  const selectedPort = useMemo(() => (
    kpis.ports.find((port) => port.portCode === selectedPortCode) || kpis.ports[0] || null
  ), [kpis.ports, selectedPortCode]);

  function openPort(port) {
    setSelectedPortCode(port.portCode);
    setActiveTab('port');
  }

  return (
    <div style={{ height: '100%', overflow: 'auto', background: 'var(--bg)', padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 9, color: 'var(--accent)', letterSpacing: 1.5, textTransform: 'uppercase' }}>Analytics</div>
          <h1 style={{ fontFamily: 'var(--syne)', fontSize: 22, lineHeight: 1.2, marginTop: 4 }}>Cargo & Port KPI</h1>
        </div>
        <div style={{ fontSize: 10, color: error ? 'var(--orange)' : 'var(--t2)', border: '1px solid var(--b1)', background: 'var(--s1)', borderRadius: 'var(--radius-sm)', padding: '6px 9px' }}>
          {loading ? 'Loading live operations data' : error ? 'Using demo fallback data' : 'Live operations data'}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflow: 'auto' }}>
        <button onClick={() => setActiveTab('overview')} style={{
          background: activeTab === 'overview' ? 'var(--accent)' : 'var(--s1)',
          color: activeTab === 'overview' ? 'var(--on-accent)' : 'var(--t2)',
          border: '1px solid var(--b1)',
          borderRadius: 'var(--radius-sm)',
          padding: '7px 11px',
          fontSize: 10,
          letterSpacing: 1,
          whiteSpace: 'nowrap',
        }}>OVERVIEW</button>
        {selectedPort && (
          <button onClick={() => setActiveTab('port')} style={{
            background: activeTab === 'port' ? 'var(--accent)' : 'var(--s1)',
            color: activeTab === 'port' ? 'var(--on-accent)' : 'var(--t2)',
            border: '1px solid var(--b1)',
            borderRadius: 'var(--radius-sm)',
            padding: '7px 11px',
            fontSize: 10,
            letterSpacing: 1,
            whiteSpace: 'nowrap',
          }}>{selectedPort.portName.toUpperCase()}</button>
        )}
      </div>

      {activeTab === 'port' && selectedPort ? (
        <PortDetail port={selectedPort} />
      ) : (
        <>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10, marginBottom: 12 }}>
        <MetricCard label="Cargo utilisation" value={`${kpis.avgCargoUtil || 0}%`} sub={`${kpis.loadedLaneMeters.toLocaleString()} loaded lane meters`} color={kpis.avgCargoUtil >= 90 ? 'var(--green)' : kpis.avgCargoUtil >= 80 ? 'var(--yellow)' : 'var(--orange)'} />
        <MetricCard label="Open capacity" value={kpis.openCapacity.toLocaleString()} sub="lane meters available" />
        <MetricCard label="Delayed calls" value={kpis.delayedCalls} sub={`${kpis.totalCalls} total schedule/meeting calls`} color={kpis.delayedCalls ? 'var(--orange)' : 'var(--green)'} />
        <MetricCard label="Avg delay" value={`${kpis.avgDelay}h`} sub="estimated across calls" color={delayColor(kpis.avgDelay)} />
        <MetricCard label="Avg port stay" value={`${kpis.avgPortStay || 0}h`} sub="pilot to completion planning" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, alignItems: 'stretch' }}>
        <Panel title="Port Delay Ranking" right="higher bar means more delay exposure">
          <div style={{ height: 300, padding: '14px 12px 4px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kpis.ports.slice(0, 12)} margin={{ top: 8, right: 12, left: -18, bottom: 18 }} onClick={(state) => state?.activePayload?.[0]?.payload && openPort(state.activePayload[0].payload)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="portCode" />
                <YAxis />
                <Tooltip contentStyle={{ background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 6, color: 'var(--text)' }} />
                <Bar dataKey="avgDelay" name="Avg delay hours" radius={[4, 4, 0, 0]}>
                  {kpis.ports.slice(0, 12).map((port) => <Cell key={port.portCode} fill={delayColor(port.avgDelay)} cursor="pointer" />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>

        <Panel title="Cargo Utilisation Trend" right="latest noon reports or vessel profile">
          <div style={{ height: 300, padding: '14px 12px 4px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={kpis.cargoTrend} margin={{ top: 8, right: 14, left: -18, bottom: 18 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[60, 100]} />
                <Tooltip contentStyle={{ background: 'var(--s1)', border: '1px solid var(--b1)', borderRadius: 6, color: 'var(--text)' }} />
                <Line type="monotone" dataKey="util" name="Cargo util %" stroke="var(--green)" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Panel>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginTop: 12 }}>
        <Panel title="High-Risk Calls" right={`${kpis.riskCalls.length} calls`}>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ color: 'var(--t3)', textTransform: 'uppercase', letterSpacing: 1, fontSize: 8 }}>
                  {['Vessel', 'Port', 'Delay', 'Reason'].map((h) => <th key={h} style={{ textAlign: 'left', padding: '10px 12px', borderBottom: '1px solid var(--b1)' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {kpis.riskCalls.slice(0, 8).map((call, index) => (
                  <tr key={`${call.vessel}-${call.portCode}-${index}`} style={{ borderBottom: '1px solid rgba(30,48,72,.55)' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 700 }}>{call.vessel || 'TBC'}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--t2)' }}>{call.portName || call.portCode || 'TBC'}</td>
                    <td style={{ padding: '10px 12px', color: delayColor(call.delayHours), fontWeight: 700 }}>{call.estDelay || `${call.delayHours}h`}</td>
                    <td style={{ padding: '10px 12px', color: 'var(--t2)' }}>{(call.reasons || []).join(', ') || call.status || 'Monitor'}</td>
                  </tr>
                ))}
                {!kpis.riskCalls.length && (
                  <tr><td colSpan="4" style={{ padding: 22, color: 'var(--t3)', textAlign: 'center' }}>No high-risk calls detected.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel title="Port Performance Detail" right="click a port to open its detail tab">
          <div style={{ display: 'grid', gap: 8, padding: 12, maxHeight: 420, overflow: 'auto' }}>
            {kpis.ports.map((port) => (
              <button key={port.portCode} onClick={() => openPort(port)} style={{ display: 'grid', gridTemplateColumns: '1fr 72px 72px 72px 72px', gap: 8, alignItems: 'center', background: selectedPortCode === port.portCode ? 'rgba(0,212,255,.08)' : 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 6, padding: 10, color: 'var(--text)', textAlign: 'left', cursor: 'pointer' }}>
                <div>
                  <div style={{ fontFamily: 'var(--syne)', fontSize: 12, fontWeight: 700 }}>{port.portName}</div>
                  <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 2 }}>{port.calls} calls - {port.cargoUtil ? `${port.cargoUtil}% cargo` : 'no cargo link'}</div>
                </div>
                <div style={{ textAlign: 'right', color: delayColor(port.avgDelay), fontWeight: 700 }}>{port.avgDelay}h</div>
                <div style={{ textAlign: 'right', color: 'var(--t2)' }}>{port.avgDwell || '-'}h</div>
                <div style={{ textAlign: 'right', color: port.risks ? 'var(--red)' : 'var(--green)', fontWeight: 700 }}>{port.risks}</div>
                <div style={{ textAlign: 'right', color: 'var(--t2)' }}>{port.openCapacity ? port.openCapacity.toLocaleString() : '-'}</div>
              </button>
            ))}
          </div>
        </Panel>
      </div>
        </>
      )}
    </div>
  );
}
